'use client';

import { useEffect, useMemo, useState } from 'react';
import { emitLoadMoreClick } from '@components/load-more-articles-button/_analytics/events';
import { LINK_PREVENT_SCROLL_TO_TOP_PROPS } from '@constants/link';
import { PAGINATION_SIZE } from '@constants/page';
import { prependBasePath } from '@helpers/base-path/base-path';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import {
    RegularAnchor,
    RegularAnchorText,
} from '@royalaholddelhaize/design-system-pantry-web/components/button/regular-button/regular-button';
import css from './load-more-articles-button.module.scss';

interface LoadMoreArticlesButtonProps {
    text: string;
}

export const LoadMoreArticlesButton: React.FC<LoadMoreArticlesButtonProps> = ({
    text,
}) => {
    const [loading, setLoading] = useState(false);
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const loadMorePath = useMemo(() => {
        const size = searchParams.get('size') || PAGINATION_SIZE;
        // eslint-disable-next-line compat/compat
        const newParams = new URLSearchParams(searchParams);
        newParams.set('size', `${Number(size) + PAGINATION_SIZE}`);
        return prependBasePath(`${pathname}?${newParams}`);
    }, [pathname, searchParams]);

    const handleClick = () => {
        setLoading(true);
        emitLoadMoreClick(text, loadMorePath);
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        // Reset loading state when the params changes
        setLoading(false);
    }, [searchParams]);

    return (
        <RegularAnchor
            className={css.button}
            href={loadMorePath}
            onClick={handleClick}
            loading={loading}
            as={Link}
            {...LINK_PREVENT_SCROLL_TO_TOP_PROPS}
        >
            <RegularAnchorText>{text}</RegularAnchorText>
        </RegularAnchor>
    );
};
